import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PersonalPolicyPush, PersonalSpacesSource } from "@/api/admin-api"

import { updatePersonalSpacesSource } from "@/api/admin-api"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { getApiErrorMessage } from "@/lib/api-error-message"

import { PersonalWindowWidenDialog } from "./personal-policy-dialogs"
import {
  isWidening,
  parseHistoryWindowDays,
  PERSONAL_HISTORY_WINDOW_DEFAULT_DAYS,
  PERSONAL_HISTORY_WINDOW_MAX_DAYS,
  PERSONAL_HISTORY_WINDOW_MIN_DAYS,
  widenRequirementFromError,
} from "./personal-policy-utils"

type PersonalSourceFeatureFormProps = {
  source: PersonalSpacesSource
  /** The stored feature: only `enabled` and `config.history_window_days` are read. */
  stored: { enabled?: boolean; config?: Record<string, unknown> }
  isLoading: boolean
  onCancel: () => void
  /** Called after a successful save with what happened to the push to FILTH. */
  onSaved: (push: PersonalPolicyPush | undefined) => void
}

function storedWindowDays(config: Record<string, unknown> | undefined): number {
  const value = config?.history_window_days
  return typeof value === "number" && Number.isInteger(value)
    ? value
    : PERSONAL_HISTORY_WINDOW_DEFAULT_DAYS
}

/**
 * The form for one `personal_source_*` feature. These features deny by default and
 * every change reaches FILTH, so they never go through the raw JSON editor: enablement
 * and the history window are saved in one validated call, raising the window needs the
 * admin's explicit confirmation (criterion 20), and the server's answer about the push
 * to FILTH is handed back to the caller.
 */
export function PersonalSourceFeatureForm({
  source,
  stored,
  isLoading,
  onCancel,
  onSaved,
}: PersonalSourceFeatureFormProps) {
  const { t } = useTranslation()
  const currentDays = storedWindowDays(stored.config)
  const currentEnabled = stored.enabled === true
  const [enabled, setEnabled] = useState(currentEnabled)
  const [daysText, setDaysText] = useState(String(currentDays))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [widen, setWiden] = useState<{ current: number; requested: number } | null>(null)

  const days = parseHistoryWindowDays(daysText)
  const windowChanged = days !== null && days !== currentDays
  const enabledChanged = enabled !== currentEnabled
  const sourceName = t(`adminDashboard.personalSources.source.${source}`)

  const save = async (widenConfirmed: boolean) => {
    if (days === null) return
    setSaving(true)
    setError(null)
    try {
      const result = await updatePersonalSpacesSource(source, {
        ...(enabledChanged ? { enabled } : {}),
        ...(windowChanged ? { history_window_days: days } : {}),
        ...(windowChanged && widenConfirmed ? { widen_confirmed: true } : {}),
      })
      setWiden(null)
      toast.success(
        t("adminDashboard.personalSources.form.saved", "{{source}} settings saved", {
          source: sourceName,
        })
      )
      onSaved(result.policy_push)
    } catch (err) {
      const requirement = widenRequirementFromError(err)
      if (requirement) {
        // The window moved under the admin: ask again against the value the server has.
        setWiden({ current: requirement.currentDays ?? currentDays, requested: days })
      } else {
        setWiden(null)
        setError(
          getApiErrorMessage(
            err,
            t("adminDashboard.featureEdit.errorSaveFailed", { defaultValue: "Save failed" })
          )
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (days === null || (!windowChanged && !enabledChanged)) return
    if (windowChanged && isWidening(currentDays, days)) {
      setWiden({ current: currentDays, requested: days })
      return
    }
    void save(false)
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <p className="text-muted-foreground text-sm">
        {t(
          "adminDashboard.personalSources.form.description",
          "Who may use {{source}} is set with Access policy on the feature list. Nobody can until a group is listed there.",
          { source: sourceName }
        )}
      </p>

      <div className="flex items-center justify-between gap-4 rounded-md border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="personal-source-enabled">
            {t("adminDashboard.featureEdit.fieldEnabled", { defaultValue: "Enabled" })}
          </Label>
          <p className="text-muted-foreground text-xs">
            {t(
              "adminDashboard.personalSources.form.enabledHelp",
              "Turning this off hides every user's {{source}} from search at once. Their data is deleted after the grace period unless it is turned back on in time.",
              { source: sourceName }
            )}
          </p>
        </div>
        <Switch checked={enabled} id="personal-source-enabled" onCheckedChange={setEnabled} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="personal-source-window">
          {t("adminDashboard.personalSources.form.window", "History window (days)")}
        </Label>
        <Input
          aria-invalid={days === null}
          className="w-32"
          id="personal-source-window"
          inputMode="numeric"
          max={PERSONAL_HISTORY_WINDOW_MAX_DAYS}
          min={PERSONAL_HISTORY_WINDOW_MIN_DAYS}
          type="number"
          value={daysText}
          onChange={(event) => setDaysText(event.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {days === null
            ? t(
                "adminDashboard.personalSources.form.windowInvalid",
                "Enter a whole number of days from {{min}} to {{max}}.",
                { min: PERSONAL_HISTORY_WINDOW_MIN_DAYS, max: PERSONAL_HISTORY_WINDOW_MAX_DAYS }
              )
            : t(
                "adminDashboard.personalSources.form.windowHelp",
                "Only items from the last {{days}} days are indexed. Raising this asks for your confirmation; lowering it does not.",
                { days: currentDays }
              )}
        </p>
      </div>

      {error ? (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      ) : null}

      <DialogFooter>
        <Button disabled={saving} type="button" variant="outline" onClick={onCancel}>
          {t("adminDashboard.featureEdit.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          disabled={saving || isLoading || days === null || (!windowChanged && !enabledChanged)}
          type="submit"
        >
          {saving ? (
            <>
              <Spinner />
              {t("adminDashboard.featureEdit.saving", { defaultValue: "Saving…" })}
            </>
          ) : (
            t("adminDashboard.featureEdit.save", { defaultValue: "Save" })
          )}
        </Button>
      </DialogFooter>

      <PersonalWindowWidenDialog
        currentDays={widen?.current ?? null}
        open={widen !== null}
        pending={saving}
        requestedDays={widen?.requested ?? null}
        source={source}
        onCancel={() => setWiden(null)}
        onConfirm={() => void save(true)}
      />
    </form>
  )
}
